import { Head, Link } from "@inertiajs/react";
import { BlogLayout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { blogPosts } from "@/data/blogPosts";
import { ArrowLeft, ArrowRight, Clock, Sparkles } from "lucide-react";
import { useEffect } from "react";
import posthog from "posthog-js";

interface Props {
  slug: string;
}

function renderMarkdown(content: string) {
  const lines = content.split("\n");
  const elements: React.ReactNode[] = [];
  let inTable = false;
  let tableRows: string[][] = [];
  let tableHeader: string[] = [];

  function flushTable() {
    if (tableHeader.length > 0) {
      elements.push(
        <div key={`table-${elements.length}`} className="my-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                {tableHeader.map((cell, i) => (
                  <th
                    key={i}
                    className="px-3 py-2 text-left font-semibold text-foreground"
                  >
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row, ri) => (
                <tr key={ri} className="border-b border-border/50">
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      className="px-3 py-2 text-muted-foreground"
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    inTable = false;
    tableRows = [];
    tableHeader = [];
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;

    // Table detection
    if (line.startsWith("|") && line.endsWith("|")) {
      const cells = line
        .split("|")
        .slice(1, -1)
        .map((c) => c.trim());
      if (!inTable) {
        inTable = true;
        tableHeader = cells;
        continue;
      }
      // Skip separator line
      if (cells.every((c) => /^[-:]+$/.test(c))) continue;
      tableRows.push(cells);
      continue;
    } else if (inTable) {
      flushTable();
    }

    // Heading
    if (line.startsWith("## ")) {
      elements.push(
        <h2
          key={i}
          className="mb-4 mt-10 text-2xl font-bold tracking-tight text-foreground"
        >
          {line.slice(3)}
        </h2>
      );
      continue;
    }
    if (line.startsWith("### ")) {
      elements.push(
        <h3
          key={i}
          className="mb-3 mt-8 text-lg font-semibold text-foreground"
        >
          {line.slice(4)}
        </h3>
      );
      continue;
    }

    // Unordered list
    if (line.startsWith("- ")) {
      const listItems: string[] = [line.slice(2)];
      while (i + 1 < lines.length && lines[i + 1]!.startsWith("- ")) {
        i++;
        listItems.push(lines[i]!.slice(2));
      }
      elements.push(
        <ul
          key={i}
          className="my-4 ml-4 list-disc space-y-2 text-muted-foreground marker:text-primary/50"
        >
          {listItems.map((item, j) => (
            <li key={j}>
              <InlineMarkdown text={item} />
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // Ordered list
    if (/^\d+\.\s/.test(line)) {
      const listItems: string[] = [line.replace(/^\d+\.\s/, "")];
      while (i + 1 < lines.length && /^\d+\.\s/.test(lines[i + 1]!)) {
        i++;
        listItems.push(lines[i]!.replace(/^\d+\.\s/, ""));
      }
      elements.push(
        <ol
          key={i}
          className="my-4 ml-4 list-decimal space-y-2 text-muted-foreground marker:text-primary/50"
        >
          {listItems.map((item, j) => (
            <li key={j}>
              <InlineMarkdown text={item} />
            </li>
          ))}
        </ol>
      );
      continue;
    }

    // Empty line
    if (line.trim() === "") continue;

    // Paragraph
    elements.push(
      <p key={i} className="my-4 leading-relaxed text-muted-foreground">
        <InlineMarkdown text={line} />
      </p>
    );
  }

  if (inTable) flushTable();

  return elements;
}

function InlineMarkdown({ text }: { text: string }) {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Bold
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    if (boldMatch && boldMatch.index !== undefined) {
      if (boldMatch.index > 0) {
        parts.push(
          <span key={key++}>{remaining.slice(0, boldMatch.index)}</span>
        );
      }
      parts.push(
        <strong key={key++} className="font-semibold text-foreground">
          {boldMatch[1]}
        </strong>
      );
      remaining = remaining.slice(boldMatch.index + boldMatch[0].length);
      continue;
    }

    // Italic
    const italicMatch = remaining.match(/\*(.+?)\*/);
    if (italicMatch && italicMatch.index !== undefined) {
      if (italicMatch.index > 0) {
        parts.push(
          <span key={key++}>{remaining.slice(0, italicMatch.index)}</span>
        );
      }
      parts.push(<em key={key++}>{italicMatch[1]}</em>);
      remaining = remaining.slice(italicMatch.index + italicMatch[0].length);
      continue;
    }

    parts.push(<span key={key++}>{remaining}</span>);
    break;
  }

  return <>{parts}</>;
}

export default function Show({ slug }: Props) {
  const post = blogPosts.find((p) => p.slug === slug);

  useEffect(() => {
    if (post) {
      posthog.capture("blog_post_viewed", {
        slug: post.slug,
        title: post.title,
        category: post.category,
      });
    }
  }, [post]);

  if (!post) {
    return (
      <BlogLayout title="Post não encontrado">
        <div className="mx-auto max-w-3xl px-4 py-20 text-center">
          <h1 className="text-2xl font-bold">Post não encontrado</h1>
          <p className="mt-2 text-muted-foreground">
            O artigo que você procura não existe.
          </p>
          <Button asChild className="mt-6">
            <Link href="/blog">Voltar ao blog</Link>
          </Button>
        </div>
      </BlogLayout>
    );
  }

  const relatedPosts = blogPosts
    .filter((p) => p.slug !== slug)
    .filter(
      (p) => p.category === post.category || p.tags.some((t) => post.tags.includes(t))
    )
    .slice(0, 3);

  const postUrl = `https://fotocerta.com/blog/${post.slug}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.metaDescription,
    url: postUrl,
    datePublished: post.publishedAt,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": postUrl,
    },
    author: {
      "@type": "Organization",
      name: "fotoCerta",
      url: "https://fotocerta.com",
    },
    publisher: {
      "@type": "Organization",
      name: "fotoCerta",
      url: "https://fotocerta.com",
    },
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Blog",
        item: "https://fotocerta.com/blog",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: post.title,
        item: postUrl,
      },
    ],
  };

  return (
    <BlogLayout title={post.metaTitle}>
      <Head>
        <meta name="description" content={post.metaDescription} />
        <meta property="og:title" content={post.metaTitle} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={postUrl} />
        <meta property="og:image" content="https://fotocerta.com/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="pt_BR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.metaTitle} />
        <meta name="twitter:description" content={post.metaDescription} />
        <meta name="twitter:image" content="https://fotocerta.com/og-image.png" />
        <meta
          property="article:published_time"
          content={post.publishedAt}
        />
        {post.tags.map((tag) => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}
        <link rel="canonical" href={postUrl} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
        {post.faq && post.faq.length > 0 && (
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": post.faq.map((item) => ({
              "@type": "Question",
              "name": item.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": item.answer,
              },
            })),
          })}</script>
        )}
      </Head>

      <article className="mx-auto max-w-3xl px-4 py-10">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <Link
            href="/blog"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft size={14} />
            Voltar ao blog
          </Link>
        </nav>

        {/* Header */}
        <header className="mb-10">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <time dateTime={post.publishedAt}>
              {new Date(post.publishedAt + "T12:00:00").toLocaleDateString(
                "pt-BR",
                {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                }
              )}
            </time>
            <span className="flex items-center gap-1">
              <Clock size={14} />
              {post.readingTime} min de leitura
            </span>
          </div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight md:text-4xl">
            {post.title}
          </h1>
          <p className="mt-3 text-lg text-muted-foreground">{post.excerpt}</p>
        </header>

        {/* Content */}
        <div className="blog-content">{renderMarkdown(post.content)}</div>

        {/* FAQ */}
        {post.faq && post.faq.length > 0 && (
          <section className="mt-10 border-t border-border pt-8">
            <h2 className="mb-6 text-2xl font-bold tracking-tight">Perguntas Frequentes</h2>
            <div className="space-y-6">
              {post.faq.map((item, i) => (
                <details key={i} className="group rounded-lg border border-border bg-card">
                  <summary className="cursor-pointer px-5 py-4 text-base font-semibold text-foreground transition-colors hover:text-primary">
                    {item.question}
                  </summary>
                  <p className="px-5 pb-4 leading-relaxed text-muted-foreground">
                    {item.answer}
                  </p>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <Card className="my-12 border-primary/20 bg-primary/5 p-6 text-center md:p-8">
          <div className="flex items-center justify-center gap-2 text-primary">
            <Sparkles size={20} />
          </div>
          <h3 className="mt-3 text-xl font-bold">
            Descubra como suas fotos são percebidas
          </h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            No fotoCerta, pessoas reais avaliam suas fotos em atratividade,
            confiança e diversão. Feedback honesto pra montar o perfil
            perfeito.
          </p>
          <Button asChild className="mt-4">
            <Link href="/">Testar Minhas Fotos</Link>
          </Button>
        </Card>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 border-t border-border pt-6">
          {post.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Related posts */}
        {relatedPosts.length > 0 && (
          <section className="mt-12 border-t border-border pt-10">
            <h2 className="mb-6 text-xl font-bold">Artigos relacionados</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {relatedPosts.map((related) => (
                <Link
                  key={related.slug}
                  href={`/blog/${related.slug}`}
                  className="group"
                >
                  <Card className="flex h-full flex-col border-border bg-card p-4 transition-colors hover:border-primary/30">
                    <h3 className="text-sm font-semibold leading-snug transition-colors group-hover:text-primary">
                      {related.title}
                    </h3>
                    <p className="mt-2 line-clamp-2 flex-1 text-xs text-muted-foreground">
                      {related.excerpt}
                    </p>
                    <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary">
                      Ler mais <ArrowRight size={12} />
                    </span>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </BlogLayout>
  );
}
